import React, { useEffect, useRef, useState } from 'react';
import { FiLogOut } from 'react-icons/fi';
import api from '../../utils/api';
import { useAuthStore } from '../../utils/store';
import { canPoll } from '../../utils/requestGovernor';

const POLL_INTERVAL_MS = 30_000; // check every 30 seconds
const COUNTDOWN_SECONDS = 20;

export const SessionWatchdog = () => {
  const { isAuthenticated, logout } = useAuthStore();
  const [kicked, setKicked] = useState(false);
  const [seconds, setSeconds] = useState(COUNTDOWN_SECONDS);
  const countdownRef = useRef(null);
  const pollRef     = useRef(null);

  const handleKick = () => {
    if (kicked) return;
    setKicked(true);
    logout?.();
  };

  const doRedirect = () => {
    window.location.href = '/login';
  };

  // Start countdown modal
  useEffect(() => {
    if (!kicked) return;
    setSeconds(COUNTDOWN_SECONDS);
    countdownRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(countdownRef.current);
          doRedirect();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [kicked]);

  // Heartbeat polling — only while authenticated and not already kicked
  useEffect(() => {
    if (!isAuthenticated || kicked) return;

    const ping = async () => {
      // This is a low-cost, low-frequency ping (30s), but every polling loop
      // in the app respects the same shared client-side budget so the app
      // never collectively trips the server's rate limit — see requestGovernor.js.
      if (!canPoll()) return;
      try {
        // silent: true — this is a background heartbeat the user never
        // triggered; if it gets rate-limited, that's not something they need
        // to be alarmed about (see api.js's response interceptor).
        await api.get('/auth/ping', { silent: true });
      } catch {
        // SESSION_REPLACED is handled by the api interceptor which fires the event
      }
    };

    pollRef.current = setInterval(ping, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [isAuthenticated, kicked]);

  // Listen for the global event fired by the api interceptor
  useEffect(() => {
    const handler = () => handleKick();
    window.addEventListener('session-replaced', handler);
    return () => window.removeEventListener('session-replaced', handler);
  }, [kicked]);

  if (!kicked) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center space-y-5">
        <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
          <FiLogOut size={28} className="text-amber-600" />
        </div>

        <div className="space-y-2">
          <h2 className="text-xl font-bold text-gray-900">Logged Out Automatically</h2>
          <p className="text-gray-600 leading-relaxed text-sm">
            Your account has been signed in on another device. You have been logged out automatically.
            <br /><br />
            <span className="font-medium text-gray-800">
              Please use only one device at a time.
            </span>
          </p>
        </div>

        <div className="text-sm text-gray-400">
          Redirecting to login in{' '}
          <span className="font-bold text-amber-600">{seconds}s</span>…
        </div>

        <button
          onClick={doRedirect}
          className="w-full py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition"
        >
          Login Now
        </button>
      </div>
    </div>
  );
};
